/**
 * Presentation helpers shared by the list and the map popups, so a venue reads
 * identically wherever it appears.
 */

const MONTHS = [
  'gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno',
  'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre',
];

export const STATUS_LABELS = {
  aperto: 'Aperto',
  chiuso: 'Chiuso',
  /*
   * Chiuso adesso, non chiuso per sempre — e la differenza conta per chi
   * legge. "Chiuso" su una scheda vuol dire non andarci mai piu';
   * "temporaneamente" vuol dire riprova fra un mese. La mappa distingue le
   * due cose e prima buttavamo via la distinzione trattandola come dubbio.
   */
  chiuso_temporaneamente: 'Chiuso temporaneamente',
  ignoto: 'Stato non verificato',
};

/**
 * Text as a search should compare it: lower case, and without accents.
 *
 * Somebody typing on a phone writes "citta" and "perche"; the catalogue is
 * full of "Città" and "Perché". Compared as typed, a venue sitting right
 * there does not come up, and the visitor concludes it is not in the
 * catalogue. Decomposing and dropping the combining marks makes the two
 * spellings the same string.
 */
export function fold(text) {
  return String(text ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}

/**
 * The folded text, plus the way back to the original.
 *
 * Folding changes lengths — one accented character becomes a letter plus a
 * combining mark, and the mark is then dropped — so a position found in the
 * folded string does not point at the same place in the text a person reads.
 * Folding one character at a time keeps a map: `starts[i]` is where the i-th
 * folded character came from. Iterating by code point rather than by code unit
 * so that a character outside the basic plane is never cut in half.
 *
 * The array gets one extra entry past the end, so a match running to the last
 * character still has somewhere to stop.
 */
function foldWithMap(text) {
  let folded = '';
  const starts = [];
  let at = 0;
  for (const character of text) {
    const piece = fold(character);
    for (let k = 0; k < piece.length; k += 1) starts.push(at);
    folded += piece;
    at += character.length;
  }
  starts.push(text.length);
  return { folded, starts };
}

/**
 * The text, with every occurrence of the query wrapped in a `mark`.
 *
 * Returned as nodes rather than as a string of markup: names in this catalogue
 * are hand-typed and a name is a name, not HTML. A card that says why it came
 * up answers a question the visitor would otherwise have to guess at — a
 * result can match on the zone, or on the name the place had on television,
 * and nothing on screen used to say which.
 */
export function highlight(text, query) {
  const fragment = document.createDocumentFragment();
  const source = String(text ?? '');
  const wanted = fold(query ?? '').trim();

  if (!wanted) {
    fragment.append(document.createTextNode(source));
    return fragment;
  }

  const { folded, starts } = foldWithMap(source);
  let cursor = 0;
  let from = 0;

  for (;;) {
    const hit = folded.indexOf(wanted, from);
    if (hit === -1) break;
    const start = starts[hit];
    const end = starts[hit + wanted.length];
    if (start > cursor) {
      fragment.append(document.createTextNode(source.slice(cursor, start)));
    }
    fragment.append(el('mark', null, source.slice(start, end)));
    cursor = end;
    from = hit + wanted.length;
  }

  fragment.append(document.createTextNode(source.slice(cursor)));
  return fragment;
}

/** Small DOM builder — every value goes in as text, never as markup. */
export function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined && text !== null) node.textContent = text;
  return node;
}

/**
 * Renders the episode date at the precision we actually have: a full date for
 * "giorno", month and year for "mese", nothing at all when the date is unknown.
 */
export function formatEpisodeDate(puntata) {
  if (!puntata?.data) return null;
  const [year, month, day] = puntata.data.split('-');
  if (!month) return year;
  const monthName = MONTHS[Number(month) - 1] ?? month;
  if (puntata.precisione === 'giorno' && day) {
    return `${Number(day)} ${monthName} ${year}`;
  }
  return `${monthName} ${year}`;
}

/** A plain ISO date in Italian words: '2026-09-08' becomes '8 settembre 2026'. */
export function formatDate(iso) {
  if (!iso) return null;
  const [year, month, day] = iso.split('-');
  if (!month) return year;
  const monthName = MONTHS[Number(month) - 1] ?? month;
  if (!day) return `${monthName} ${year}`;
  return `${Number(day)} ${monthName} ${year}`;
}

/** One-line episode description, e.g. 'Miglior osteria — 15 maggio 2025'. */
export function formatEpisode(puntata) {
  const parts = [puntata?.titolo, formatEpisodeDate(puntata)].filter(Boolean);
  return parts.length ? parts.join(' — ') : 'Puntata non identificata';
}

/**
 * Outcome badge. Only rendered when we hold a verified result — a contestant
 * whose placing we could not confirm gets no badge rather than a made-up one.
 */
export function outcomeLabel(venue) {
  if (venue.vincitore) return 'Vincitore della puntata';
  if (venue.posizione) return `${venue.posizione}º classificato`;
  // There was a third fallback here, `venue.esito`, a free-text placing that
  // no row ever carried — null on all forty. The column is gone; so is the
  // branch, which could only ever have returned nothing.
  return null;
}

/**
 * Most recent episodes first; venues with no known date sink to the bottom.
 * ISO date strings compare correctly as plain strings, partial ones included.
 */
export function byEpisodeDateDesc(a, b) {
  const dateA = a.puntata?.data ?? '';
  const dateB = b.puntata?.data ?? '';
  if (dateA === dateB) return (a.nome ?? '').localeCompare(b.nome ?? '', 'it');
  if (!dateA) return 1;
  if (!dateB) return -1;
  return dateB.localeCompare(dateA);
}

/** A zone gets a page of its own from this many venues up. */
export const MIN_ZONE = 4;

/**
 * "Tutti i 17", "tutti gli 8": the article follows how the number is said.
 * Zones are named without a preposition, as in `worker/pagine.js`: "a Lago
 * di Garda" is wrong where "a Genova" is right.
 */
export function allOf(n) {
  return /^8/.test(String(n)) || n === 11 ? `Tutti gli ${n}` : `Tutti i ${n}`;
}

/**
 * A programme's name with who presents it, where that is certain. The Worker
 * puts the same name on the programme's page with its own copy in
 * `worker/pagine.js`; the two must agree, or the heading changes under the
 * reader the moment the app takes over.
 */
const PROGRAM_HOSTS = {
  '4-ristoranti': 'Alessandro Borghese',
  'cucine-da-incubo': 'Antonino Cannavacciuolo',
  foodish: 'Joe Bastianich',
  'food-advisor': 'Simone Rugiati',
};

/** Who presents a programme, or nothing where it is not certain. */
export function programHost(programId) {
  return PROGRAM_HOSTS[programId] ?? null;
}

export function programLongName(programId, programName) {
  // The guide's screen says what it holds (`programLongName` in `worker/pagine.js`).
  if (programId === 'stellati-michelin') return 'Ristoranti stellati Michelin 2026';
  const host = PROGRAM_HOSTS[programId];
  return host ? `${programName} di ${host}` : programName;
}

/**
 * The address of a zone: 'Lago di Garda' is `lago-di-garda`, 'Forlì' is
 * `forli`. The Worker writes the same links with its own copy in
 * `worker/pagine.js`; the two must agree.
 */
export function zoneSlug(zona) {
  return fold(zona)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
